import { defineComponent, ref, watch, onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useI18n } from 'vue-i18n';
import { useIdle } from '@vueuse/core';
import axios from 'axios';
import isEmpty from 'lodash-es/isEmpty';
import { NOTICE_TARGET_WEB_VALUES } from '@hems/admin/src/config/noticeConfig';
import NoticeModal from '@hems/component/src/modal/NoticeModal.vue';
import IdleTimePopup from '@hems/component/src/popup/IdleTimePopup.vue';
import PrivacyPolicyModal from '@hems/container/src/terms/PrivacyPolicyModal.vue';
import LoginButton from '@hems/portal/src/components/button/LoginButton.vue';
import Footer from '@hems/portal/src/components/footer/Footer.vue';
import Header from '@hems/portal/src/components/header/Header.vue';
import MobileAppLinkModal from '@hems/portal/src/components/modal/MobileAppLinkModal.vue';
import { useAuthentication } from '@hems/portal/src/composables/authentication';
import { useDashboard } from '@hems/portal/src/composables/dashboard';
import { CommonService } from '@hems/service';
import { Helper } from '@hems/util';
import { useLanguage } from '@hems/util/src/composable/language';
import { useMSAL } from '@hems/util/src/composable/msal';
import { ONE_HOUR_VALUE_IN_MILLISECONDS } from '@hems/util/src/constant/date';
import { LANGUAGE } from '@hems/util/src/constant/languages';
import { localStorageKey } from '@hems/util/src/constant/localStorageKey';
import { TENANT } from '@hems/util/src/constant/msal';
import { getCurrentTimestamp } from '@hems/util/src/helper/dateHelper';
const API_BASE_URL = process.env.VUE_APP_API_URL;
export default defineComponent({
    name: 'Home',
    components: {
        Footer,
        Header,
        LoginButton,
    },
    setup() {
        const { locale: i18nLocale } = useI18n();
        const { setLanguage } = useLanguage();
        const { idle } = useIdle(ONE_HOUR_VALUE_IN_MILLISECONDS);
        const { isLogin } = useAuthentication();
        const { dashboardUrl } = useDashboard();
        const noticeInfo = ref(null);
        const isImageLoaded = ref(false);
        const axiosInstance = axios.create({
            baseURL: API_BASE_URL,
        });
        const commonService = new CommonService(axiosInstance);
        const changeLanguage = (selectedLanguage) => {
            i18nLocale.value = selectedLanguage;
            setLanguage(selectedLanguage);
        };
        const mobileAppLinkModal = useModal({
            component: MobileAppLinkModal,
        });
        const openMobileAppLinkModal = (type) => {
            mobileAppLinkModal.patchOptions({
                attrs: {
                    storeType: type,
                },
            });
            mobileAppLinkModal.open();
        };
        const privacyPolicyModal = useModal({
            component: PrivacyPolicyModal,
            attrs: {
                onClose: () => {
                    privacyPolicyModal.close();
                },
            },
        });
        const openPrivacyPolicyModal = () => {
            privacyPolicyModal.open();
        };
        const noticeModal = useModal({
            component: NoticeModal,
        });
        const openNoticeModal = () => {
            if (!noticeInfo.value)
                return;
            noticeModal.patchOptions({
                attrs: {
                    noticeInfo: noticeInfo.value,
                    onClose: () => {
                        noticeModal.close();
                    },
                },
            });
            noticeModal.open();
        };
        const getNotice = async () => {
            // 1. API 호출로 공지 여부(utc 시간으로 비교) 및 내용 가져오기
            // 2. localStorage에서 공지 Expired Time 비교하여 notice 표시 여부 확인
            try {
                /**
                 *  공지 API 호출
                 * GET /user/notice
                 * @params lang ('en', 'de', 'fr', 'ko')
                 * @response { title: string, contents: string, notice_target: string,
                 *   notice_start_dt: string, notice_end_dt: string }
                 */
                // 우선 공지 언어는 영어만 가능하도록 처리 추후 변경 필요 시 재수정
                const notice = await commonService.getNotice(LANGUAGE.EN);
                // 공지가 없는 경우 localStorage에 저장한 공지 Expired Time 삭제 / 공지 띄우지 않음
                if (isEmpty(notice) && !Helper.isNull(localStorage.getItem(localStorageKey.notice))) {
                    localStorage.setItem(localStorageKey.notice, '');
                }
                const isNoticeTarget = NOTICE_TARGET_WEB_VALUES.includes(notice?.notice_target); // 공지 target 여부
                if (isNoticeTarget) {
                    noticeInfo.value = notice;
                    const expiredTime = localStorage.getItem(localStorageKey.notice); // 하루동안 다시 보지 않기의 만료 시간
                    // 현재 시간이 Expired Time 이후 (사용자가 Close 버튼을 클릭하고 24시간 후) 라면 공지를 띄움
                    const showNotice = !Helper.isNull(expiredTime) ? getCurrentTimestamp() > Number(expiredTime) : true;
                    if (showNotice) {
                        openNoticeModal();
                    }
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const { logoutRedirect, loginRedirect, getAuthRedirectPromiseResponse, acquireToken, processLogin } = useMSAL();
        const idleTimeModal = useModal({
            component: IdleTimePopup,
            attrs: {
                onClose: () => {
                    logoutRedirect();
                },
                onConfirm: () => {
                    idleTimeModal.close();
                },
            },
        });
        const onLogout = async () => {
            idleTimeModal.close();
            logoutRedirect();
        };
        const login = async (tenant) => {
            loginRedirect(tenant);
        };
        const onClickLoginButton = (tenant) => {
            if (isLogin.value) {
                window.location.href = dashboardUrl.value;
            }
            else {
                login(tenant);
            }
        };
        const handleAuthRedirectResponse = async () => {
            try {
                const { responseHomeowner, responsePartners } = await getAuthRedirectPromiseResponse();
                /** redirect 되는 시점에 homeowner, partners의 응답 값이 둘 중 하나라도 있으면 신규 로그인 */
                const authenticationResult = responseHomeowner || responsePartners;
                if (authenticationResult) {
                    processLogin({ responseHomeowner, responsePartners });
                }
                else {
                    /** 만약 토큰이 있으면 토큰만 갱신 */
                    acquireToken();
                }
            }
            catch (error) {
                console.error(error);
            }
        };
        const onLoadImage = () => {
            isImageLoaded.value = true;
        };
        watch(idle, (idleValue) => {
            if (idleValue) {
                idleTimeModal.open();
            }
        });
        getNotice();
        onMounted(() => {
            handleAuthRedirectResponse();
        });
        return {
            noticeInfo,
            isLogin,
            isImageLoaded,
            TENANT,
            changeLanguage,
            openMobileAppLinkModal,
            onLogout,
            onClickLoginButton,
            onLoadImage,
            openPrivacyPolicyModal,
        };
    },
});
